/**
 * キーボード・ポインタ入力（FR-NAV・FR-A11Y-01）。
 * 操作はすべて ViewerApi（＝公開 API と同じ動作）へ写像し、UI 独自の裏道を作らない。
 * ポインタは種別（マウス／タッチ／ペン）を区別せず、Pointer Events 一本で扱う。
 */
import type { DisposeRegistry } from '../core/dispose-registry';
import type { ViewerApi } from './api';

/** スワイプと判定する最小移動量（CSS ピクセル）。 */
const SWIPE_THRESHOLD = 40;
/** ダブルタップと判定する間隔（ms）と許容ずれ（px）。 */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP = 24;
/** ダブルタップで入る倍率。 */
const DOUBLE_TAP_ZOOM = 2;

/**
 * Shift＋矢印で動かす量（CSS ピクセル）。ドラッグ 1 回ぶんに近い手応えにする。
 * パッド（ui/pan-pad.ts）は誌面の短辺に対する割合で動かすが、キーは押しっぱなしで
 * 連射できるため一定量にしている。
 */
const PAN_KEY_STEP = 80;

/**
 * Shift＋矢印 → パン（WCAG 2.1.1）。拡大していないときは何も起きない。
 * 矢印単独はページめくりのままにする — 拡大中だけ意味が変わると、
 * 「同じキーが状況で別の動きをする」形になって覚えにくいため。
 */
export const PAN_KEY_ACTIONS: Record<string, (api: ViewerApi) => void> = {
  ArrowRight: (api) => api.panBy(-PAN_KEY_STEP, 0),
  ArrowLeft: (api) => api.panBy(PAN_KEY_STEP, 0),
  ArrowDown: (api) => api.panBy(0, -PAN_KEY_STEP),
  ArrowUp: (api) => api.panBy(0, PAN_KEY_STEP),
};

/** キー → 動作。ここに無いキーは素通しする（ブラウザ既定を奪わない）。 */
export const KEY_ACTIONS: Record<string, (api: ViewerApi) => void> = {
  ArrowRight: (api) => api.flipRight(),
  ArrowLeft: (api) => api.flipLeft(),
  PageDown: (api) => api.next(),
  PageUp: (api) => api.prev(),
  Home: (api) => api.first(),
  End: (api) => api.last(),
  '+': (api) => api.zoomIn(),
  '=': (api) => api.zoomIn(),
  '-': (api) => api.zoomOut(),
  '0': (api) => api.resetZoom(),
  '?': (api) => api.toggleHelp(),
};

/**
 * ヘルプに出すキー操作（表示順）。同義キーは 1 行にまとめる。
 * KEY_ACTIONS の全キーがここに現れることは help.test.ts が機械検証する
 * （キーを足したのにヘルプが古いまま、という腐り方を防ぐ）。
 */
export const KEY_HELP: readonly {
  keys: readonly string[];
  label: string;
  /** 同時に押す修飾キー（Shift＋矢印のパンだけが持つ）。 */
  modifier?: string;
}[] = [
  { keys: ['ArrowRight'], label: 'help.key.flipRight' },
  { keys: ['ArrowLeft'], label: 'help.key.flipLeft' },
  { keys: ['PageDown'], label: 'help.key.next' },
  { keys: ['PageUp'], label: 'help.key.prev' },
  { keys: ['Home'], label: 'help.key.first' },
  { keys: ['End'], label: 'help.key.last' },
  { keys: ['+', '='], label: 'help.key.zoomIn' },
  { keys: ['-'], label: 'help.key.zoomOut' },
  { keys: ['0'], label: 'help.key.resetZoom' },
  { keys: ['?'], label: 'help.key.help' },
  {
    keys: ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'],
    modifier: 'Shift',
    label: 'help.key.pan',
  },
  // Esc は Shell が引き取るため KEY_ACTIONS には無い（help.test.ts の OUTSIDE_ACTIONS）。
  { keys: ['Escape'], label: 'help.key.close' },
];

/** ヘルプに出すタッチ操作（bindPointer の実装と対）。 */
export const TOUCH_HELP: readonly string[] = [
  'help.touch.swipe',
  'help.touch.pinch',
  'help.touch.doubleTap',
];

function bindKeyboard(root: HTMLElement, registry: DisposeRegistry, api: ViewerApi): void {
  registry.listen(root, 'keydown', (event) => {
    const keyboard = event as KeyboardEvent;
    if (keyboard.metaKey || keyboard.ctrlKey || keyboard.altKey) return;
    // すでに内側が引き取ったキーには手を出さない。ツールバー（APG の toolbar
    // パターン）が矢印と Home/End をボタン間の移動に使うのがこれに当たる。
    if (keyboard.defaultPrevented) return;
    // 文字を打ち込む欄でのキー入力は奪わない。**複数行の欄と選択欄も含める** —
    // ここが INPUT だけだったため、コメント欄や誌面フォームで `0` `-` `+` `?` を
    // 打つとズームやヘルプが動いてしまっていた（WCAG 2.1.4・M8c-2 手順 1 で発見）。
    const target = keyboard.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
      return;
    }

    // Shift＋矢印は拡大中の表示位置の移動（WCAG 2.1.1）。等倍でも受け取って
    // 素通しさせないのは、押した結果が「拡大しているかどうか」で変わるだけで、
    // ブラウザ既定（水平スクロール）と競合させないため。
    const action = keyboard.shiftKey ? PAN_KEY_ACTIONS[keyboard.key] : KEY_ACTIONS[keyboard.key];
    if (!action) return;
    action(api);
    keyboard.preventDefault();
  });
}

/** 2 点間の距離と中点。 */
function pinchOf(points: PointerEvent[]): { distance: number; center: { x: number; y: number } } {
  const [a, b] = points as [PointerEvent, PointerEvent];
  return {
    distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
    center: { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 },
  };
}

/**
 * ポインタ操作。
 * - 1 点: 等倍ならスワイプでめくる／拡大中はドラッグでパン
 * - 2 点: ピンチで拡大縮小
 * - ダブルタップ: 拡大と等倍のトグル
 */
function bindPointer(host: HTMLElement, registry: DisposeRegistry, api: ViewerApi): void {
  const active = new Map<number, PointerEvent>();
  let start: PointerEvent | null = null;
  let dragged = false;
  let pinchStart: { distance: number; zoom: number } | null = null;
  let lastTap: { time: number; x: number; y: number } | null = null;

  /** ホスト内の相対座標（0〜1）。 */
  const focalOf = (x: number, y: number) => {
    const rect = host.getBoundingClientRect();
    return { x: (x - rect.left) / rect.width, y: (y - rect.top) / rect.height };
  };

  registry.listen(host, 'pointerdown', (event) => {
    const pointer = event as PointerEvent;
    active.set(pointer.pointerId, pointer);
    if (active.size === 1) {
      start = pointer;
      dragged = false;
      host.setPointerCapture(pointer.pointerId);
    } else if (active.size === 2) {
      pinchStart = { distance: pinchOf([...active.values()]).distance, zoom: api.getState().zoom };
    }
  });

  registry.listen(host, 'pointermove', (event) => {
    const pointer = event as PointerEvent;
    if (!active.has(pointer.pointerId)) return;
    const previous = active.get(pointer.pointerId);
    active.set(pointer.pointerId, pointer);

    if (active.size === 2 && pinchStart) {
      const { distance, center } = pinchOf([...active.values()]);
      const level = (pinchStart.zoom * distance) / pinchStart.distance;
      api.zoomTo(level, focalOf(center.x, center.y));
      dragged = true;
      return;
    }

    if (active.size === 1 && previous && api.getState().zoom > 1) {
      api.panBy(pointer.clientX - previous.clientX, pointer.clientY - previous.clientY);
      dragged = true;
    }
  });

  const release = (event: Event) => {
    const pointer = event as PointerEvent;
    const first = start;
    active.delete(pointer.pointerId);
    if (active.size < 2) pinchStart = null;
    if (active.size > 0 || !first) return;
    start = null;

    if (dragged || pointer.type === 'pointercancel') return;

    const dx = pointer.clientX - first.clientX;
    const dy = pointer.clientY - first.clientY;

    // ダブルタップ: 拡大 ⇔ 等倍
    const now = pointer.timeStamp;
    const tapped = Math.abs(dx) < DOUBLE_TAP_SLOP && Math.abs(dy) < DOUBLE_TAP_SLOP;
    if (tapped && lastTap && now - lastTap.time < DOUBLE_TAP_MS) {
      const focal = focalOf(pointer.clientX, pointer.clientY);
      if (api.getState().zoom > 1) api.resetZoom();
      else api.zoomTo(DOUBLE_TAP_ZOOM, focal);
      lastTap = null;
      return;
    }
    if (tapped) {
      lastTap = { time: now, x: pointer.clientX, y: pointer.clientY };
      return;
    }

    // スワイプ: 指を左へ動かす＝内容を左へ送る＝右側にあるページを見に行く。
    if (api.getState().zoom > 1) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) api.flipRight();
    else api.flipLeft();
  };

  registry.listen(host, 'pointerup', release);
  registry.listen(host, 'pointercancel', release);
}

export function bindInput(
  root: HTMLElement,
  host: HTMLElement,
  registry: DisposeRegistry,
  api: ViewerApi,
): void {
  bindKeyboard(root, registry, api);
  bindPointer(host, registry, api);
}
